import React from "react";
import _ from "lodash";
import { FormControl, st, AppSettings } from "lib/index";

export const WIDGET_ROUTES = [
  {
    type: "homepage",
    name: I18n.t("papp.settings.WidgetRouteFormSection.homepage_page"),
    url: ({ locale, currency, company }) => company.getHomepageUrl({ locale, currency }),
    requiredProps: [],
  },
  {
    type: "products_list",
    name: I18n.t("papp.settings.WidgetRouteFormSection.products_list_page"),
    when: ({ products }) => products.length > 0,
    url: ({ locale, currency, company, productIds }) =>
      company.getWebsiteUrl(`/widget/products_list`, {
        locale,
        currency,
        company_id: company.id,
        product_ids: productIds ? productIds.join(",") : undefined,
      }),
    requiredProps: ["productIds"],
  },
  {
    type: "products_calendar",
    name: I18n.t("papp.settings.WidgetRouteFormSection.products_calendar_page"),
    when: ({ products }) => products.length > 0,
    url: ({ locale, currency, company, productIds, onSlotClickActionType }) =>
      company.getWebsiteUrl(`/widget/products_calendar`, {
        locale,
        currency,
        company_id: company.id,
        product_ids: productIds ? productIds.join(",") : undefined,
        on_slot_click_action_type: onSlotClickActionType,
      }),
    requiredProps: ["productIds", "onSlotClickActionType"],
  },
  {
    type: "product",
    name: I18n.t("papp.settings.WidgetRouteFormSection.product_page"),
    when: ({ products }) => products.length > 0,
    url: ({ locale, currency, company, productId }) =>
      productId &&
      company.getWebsiteUrl(`/p/${productId}`, {
        locale,
        currency,
      }),
    requiredProps: ["productId"],
  },
  {
    type: "product_location_tag_page",
    name: I18n.t("papp.settings.WidgetRouteFormSection.product_location_tag_page"),
    when: ({ marketplace }) => marketplace,
    url: ({ locale, currency, company, productLocationTagId }) =>
      productLocationTagId &&
      company.getWebsiteUrl(`/t/${productLocationTagId}`, {
        locale,
        currency,
      }),
    requiredProps: ["productLocationTagId"],
  },
  {
    type: "product_category_tag_page",
    name: I18n.t("papp.settings.WidgetRouteFormSection.product_category_tag_page"),
    when: ({ marketplace }) => marketplace,
    url: ({ locale, currency, company, productCategoryTagId }) =>
      productCategoryTagId &&
      company.getWebsiteUrl(`/t/${productCategoryTagId}`, {
        locale,
        currency,
      }),
    requiredProps: ["productCategoryTagId"],
  },
  {
    type: "new_order",
    name: I18n.t("papp.settings.WidgetRouteFormSection.new_order_page"),
    when: ({ products }) => products.length > 0,
    url: ({ locale, currency, company, productId }) =>
      productId &&
      company.getWebsiteUrl(`/p/${productId}/rezerwuj`, {
        locale,
        currency,
      }),
    requiredProps: ["productId"],
  },
  {
    type: "reviews",
    name: I18n.t("papp.settings.WidgetRouteFormSection.reviews_page"),
    url: ({ locale, currency, company }) => company.getHomepageUrl({ locale, currency }) + "#reviews",
    requiredProps: [],
  },
  {
    type: "coupons",
    name: I18n.t("papp.settings.WidgetRouteFormSection.coupons_page"),
    when: ({ products }) => products.length > 0,
    url: ({ locale, currency, company }) =>
      company.getWebsiteUrl(`/widget/coupons`, {
        locale,
        currency,
      }),
    requiredProps: [],
  },
];

export function buildRouteParams({
  company,
  products,
  marketplace,
  type = "homepage",
  productId = products.length > 0 ? products[0].id : null,
  productIds = null,
  productLocationTagId,
  productCategoryTagId,
  onSlotClickActionType = "show_product",
  locale,
  currency,
}) {
  // resellers company_id must be set to null to avoid confusion on backend offer selection
  let companyFiltered = _.cloneDeep(company);
  if (companyFiltered.reseller) {
    companyFiltered.id = undefined;
  }
  return {
    type,
    productId,
    productIds,
    productLocationTagId,
    productCategoryTagId,
    onSlotClickActionType,
    url: WIDGET_ROUTES.find((route) => route.type === type).url({
      company: companyFiltered,
      productId,
      productIds,
      productLocationTagId,
      productCategoryTagId,
      onSlotClickActionType,
      locale,
      currency,
    }),
  };
}

const onSlotClickActionTypeOptions = [
  ["show_product", I18n.t("papp.settings.WidgetRouteFormSection.on_slot_click_show_product")],
  ["new_order", I18n.t("papp.settings.WidgetRouteFormSection.on_slot_click_new_order")],
];

export default class WidgetRouteFormSection extends React.PureComponent {
  componentWillReceiveProps(nextProps) {
    if (nextProps.locale !== this.props.locale || nextProps.currency !== this.props.currency) {
      this.props.onChange(
        buildRouteParams({
          company: nextProps.company,
          products: nextProps.products,
          marketplace: nextProps.marketplace,
          locale: nextProps.locale,
          currency: nextProps.currency,
          ...nextProps.value,
        })
      );
    }
  }

  handlePropChange(prop, v) {
    const { company, products, marketplace, locale, currency, value, onChange } = this.props;
    onChange(
      buildRouteParams({
        company,
        products,
        marketplace,
        locale,
        currency,
        ...value,
        [prop]: v,
      })
    );
  }

  render() {
    const { company, products, marketplace, value, onChange, className, style } = this.props;
    const productOptions =
      products && products.records && products.records.length
        ? products.records
            .sort((a, b) => (b.name.toLowerCase() < a.name.toLowerCase() ? 1 : -1))
            .map((p) => [p.id, p.name])
        : [];
    const route = WIDGET_ROUTES.find((route) => route.type === value.type) || WIDGET_ROUTES[0];
    const typeOptions = WIDGET_ROUTES.filter(({ when }) => !when || when({ company, products, marketplace })).map(
      ({ type, name }) => [type, name]
    );

    return (
      <div className={"field " + (className || "")} style={style}>
        <div className="fields grouped u-marginNone">
          <FormControl.SelectFormGroup
            label={I18n.t("papp.settings.WidgetRouteFormSection.type")}
            inlineLabel
            value={value.type}
            onChange={this.handlePropChange.bind(this, "type")}
            options={typeOptions}
            wrapperClassName="u-marginTopNone"
          />

          {route.requiredProps.includes("productId") && (
            <FormControl.SelectFormGroup
              label={I18n.t("papp.settings.WidgetRouteFormSection.product")}
              inlineLabel
              value={value.productId}
              onChange={this.handlePropChange.bind(this, "productId")}
              options={productOptions}
              search
              className="u-largeWidth"
            />
          )}

          {route.requiredProps.includes("productLocationTagId") && (
            <FormControl.SelectFormGroup
              label={I18n.t("papp.settings.WidgetRouteFormSection.product_location_tag")}
              inlineLabel
              value={value.productLocationTagId}
              onChange={this.handlePropChange.bind(this, "productLocationTagId")}
              options={AppSettings.product_location_tags.map((tag) => [tag.id, tag.name])}
              search
              className="u-largeWidth"
            />
          )}

          {route.requiredProps.includes("productCategoryTagId") && (
            <FormControl.SelectFormGroup
              label={I18n.t("papp.settings.WidgetRouteFormSection.product_category_tag")}
              inlineLabel
              value={value.productCategoryTagId}
              onChange={this.handlePropChange.bind(this, "productCategoryTagId")}
              options={AppSettings.product_category_tags.map((tag) => [tag.id, tag.name])}
              search
              className="u-largeWidth"
            />
          )}

          {route.requiredProps.includes("productIds") && (
            <FormControl.SelectLimitFormGroup
              value={value.productIds}
              onChange={this.handlePropChange.bind(this, "productIds")}
              label={I18n.t("papp.settings.WidgetRouteFormSection.all_products")}
              options={productOptions}
              initialSelectedValues={[]}
              type="MultipleCheckbox"
            />
          )}

          {route.requiredProps.includes("onSlotClickActionType") && (
            <FormControl.MultipleRadioFormGroup
              value={value.onSlotClickActionType}
              onChange={this.handlePropChange.bind(this, "onSlotClickActionType")}
              label={I18n.t("papp.settings.WidgetRouteFormSection.on_slot_click_action_type")}
              options={onSlotClickActionTypeOptions}
            />
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/settings/WidgetsSettingsView/WidgetRouteFormSection.jsx